import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { ApprovalsTab } from './ResourcePlanning';

const RESOURCES_ENABLED = process.env.REACT_APP_RESOURCES_ENABLED === 'true';

const DAY_NAMES = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Mirrors apps/api's TASK_TYPE_CATEGORY_VALUES -- apps/web can't import packages/shared (ADR 0019),
// same duplication as Configuration.js.
// Recap row order, most to least billable, with leave last -- not the constant's order.
const TASK_TYPE_CATEGORIES = [
  { value: 'Billable', label: 'Billable' },
  { value: 'NonBillableProductive', label: 'Non-Billable Productive' },
  { value: 'NonBillable', label: 'Non-Billable' },
  { value: 'TimeOff', label: 'Time Off' },
];

// Leave: non-billable, and out of both sides of the utilization rates below.
const TIME_OFF_CATEGORY = 'TimeOff';

// ---------- Pure helpers (exported for tests — see TimeEntry.test.js) ----------

// Local date key for a Date WE constructed client-side (weekDates() below) — a plain calendar day
// with no serialization round trip involved, so local getters are exactly right here.
export function dateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Converts a date-only value that came BACK from the API (a Prisma DateTime, JSON-serialized as a
// full ISO timestamp) into the same 'YYYY-MM-DD' key. Deliberately reads UTC components — the
// server always constructs/serializes date-only values as UTC midnight (ADR 0009,
// packages/shared/dates.ts), so a UTC read round-trips the calendar date correctly regardless of
// the browser's own timezone. Using local getters here would silently roll the date back a day in
// any timezone behind UTC.
export function apiDateKey(value) {
  const d = new Date(value);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfWeek(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  d.setDate(d.getDate() - ((dow + 1) % 7)); // Saturday-start week (PS timesheet week is Sat-Fri)
  return d;
}

// The week runs Sat-Fri, so the weekend is always the first two columns -- read off the date anyway,
// so a change to the week start can't leave the shading on the wrong days.
export const isWeekendDate = (d) => d.getDay() === 0 || d.getDay() === 6;

export function weekDates(anchor) {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

// Approvals badge count: timesheets (person + Sat–Fri week), not day entries. Two days of the same
// person's week is one thing to approve and one row in the queue, so counting entries overstated the
// backlog. One Set across both roles, not a sum: sequential approval (ADR 0029) means a given entry
// is only ever pending for one of them, but a merged Set also can't double-count if that changes.
export function countPendingTimesheets(result) {
  const timesheets = new Set();
  for (const entry of [...(result?.asManager || []), ...(result?.asPm || [])]) {
    const [y, m, d] = apiDateKey(entry.entryDate).split('-').map(Number);
    timesheets.add(`${entry.userId}|${dateKey(startOfWeek(new Date(y, m - 1, d)))}`);
  }
  return timesheets.size;
}

// The fiscal week(s) a visible grid week falls in, and whether they cross a fiscal MONTH boundary --
// that's the part that matters, since it's the month a row's hours get reported under.
export function fiscalWeekLabel(weeks) {
  if (!weeks || weeks.length === 0) return null;
  const text = weeks.map((w) => `${w.periodName} · ${w.periodMonth}`).join('  /  ');
  const splitsMonth = new Set(weeks.map((w) => w.periodMonth)).size > 1;
  return { text, splitsMonth };
}

// Which of the visible days sit in an archived (closed to entry) fiscal period. Per-day, not
// per-week: a Sat-Fri grid week can straddle two fiscal weeks, so the front half of a week can be
// closed while the back half is still open. The API refuses writes on these dates either way; this
// is what stops the user typing into a cell that's going to be rejected.
export function archivedDateKeys(weeks, keys) {
  const closed = new Set();
  for (const w of weeks || []) {
    if (!w.isArchived) continue;
    const start = apiDateKey(w.startDate);
    const end = w.endDate ? apiDateKey(w.endDate) : null;
    for (const key of keys) if (key >= start && (!end || key <= end)) closed.add(key);
  }
  return closed;
}

export function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// accountId is part of the identity so Travel-for-Ada and Travel-for-Sedgwick are two rows, not one
// (2026-08-11) -- both are the same (Administrative Time, Travel) pair and would otherwise collapse.
export function rowKey(projectId, taskTypeId, accountId) {
  return `${projectId}|${taskTypeId}|${accountId || ''}`;
}

// "Copy last week": the entries to create from the previous week's. Each lands on the same weekday and
// row; a day that is closed, a holiday, or already has hours for that row is left alone, so running it
// twice or on a half-filled week never overwrites anything.
export function planCopyForward(prevEntries, currentEntries, { closedKeys, holidayKeys }) {
  const taken = new Set(
    currentEntries.map((e) => `${rowKey(e.projectId, e.taskTypeId, e.accountId)}|${apiDateKey(e.entryDate)}`),
  );
  const toCreate = [];
  let skipped = 0;
  for (const e of prevEntries) {
    const target = new Date(apiDateKey(e.entryDate) + 'T00:00:00Z');
    target.setUTCDate(target.getUTCDate() + 7);
    const day = target.toISOString().slice(0, 10);
    const key = `${rowKey(e.projectId, e.taskTypeId, e.accountId)}|${day}`;
    if (closedKeys.has(day) || holidayKeys.has(day) || taken.has(key) || !(e.hours > 0)) {
      skipped += 1;
      continue;
    }
    taken.add(key);
    toCreate.push({
      projectId: e.projectId,
      taskTypeId: e.taskTypeId,
      ...(e.accountId ? { accountId: e.accountId } : {}),
      hours: e.hours,
      entryDate: day,
    });
  }
  return { toCreate, skipped };
}

// Placeholder projects (pre-SOW, forecast-only -- see Resource Planning's Forecasting view)
// don't get real timesheet hours logged against them (Jeremy, 2026-08-04), and neither do Archived
// ones (Jeremy, 2026-08-07). Only affects the "add a project" picker below -- an existing row for
// either still renders, so hours already logged (or a week still being caught up on a project
// archived mid-week) stay editable.
export function loggableProjects(projects) {
  return projects.filter((p) => !p.isPlaceholder && p.projectStatus !== 'Archived');
}

// Order for the "+ Add a project…" picker (Jeremy, 2026-08-12): the administrative project
// ("Non-Billable Time") pinned first, everything else A-Z. It's the one project every person picks
// every week, and alphabetically it lands in the middle of 20-odd customer projects. Keyed off
// `isAdministrative`, not the name, so renaming it doesn't quietly unpin it.
export function projectPickerOptions(projects) {
  return loggableProjects(projects)
    .slice()
    .sort(
      (a, b) =>
        Number(!!b.isAdministrative) - Number(!!a.isAdministrative) ||
        (a.name || '').localeCompare(b.name || ''),
    );
}

// Administrative task types (code 'ADMIN') only make sense under the Administrative Time project
// and vice versa (Jeremy, 2026-08-05) -- with no exceptions as of 2026-08-12: Travel and Post Go
// Live Support are administrative time too, and name a customer instead of a project
// (taskTypeNeedsAccount below), so they're no longer offered against a real project.
// A type carrying a projectId is scoped to that one project (e.g. MNCCC's Pilot Phase / Rollout
// 1-3); null means portfolio-wide, which is nearly all of them. Mirrors the server's
// assertTaskTypeInScope -- keep the two in step.
export function isTaskTypeAvailableForProject(taskType, isAdministrativeProject, projectId) {
  if (taskType.projectId && taskType.projectId !== projectId) return false;
  return isAdministrativeProject ? taskType.code === 'ADMIN' : taskType.code !== 'ADMIN';
}

// Travel / Post Go Live Support: administrative time done for a specific customer, where the
// Account is the only thing tying those hours to one, so it's required. Mirrors the server's
// resolveRowAccountId -- keep the two in step.
export function taskTypeNeedsAccount(taskType, isAdministrativeProject) {
  return !!taskType?.requiresAccount && !!isAdministrativeProject;
}

// A row is a (project, task type) pair (ADR 0023 — Time Entry Tasks are a global category, not a
// scheduling Task). Rows come from two sources merged together: pinned TimesheetRows (persist
// across weeks so the grid isn't rebuilt from scratch every Monday) and any real entry this week,
// even an unpinned one (e.g. a row someone hasn't gotten around to pinning yet). Administrative
// time (Project.isAdministrative) is just another project here -- no special casing.
export function buildMyTimeRows(entries, pinnedRows, projects, taskTypes, accounts = []) {
  const rows = new Map();
  const ensureRow = (projectId, taskTypeId, accountId) => {
    const key = rowKey(projectId, taskTypeId, accountId);
    if (!rows.has(key)) {
      const taskType = taskTypes.find((t) => t.id === taskTypeId);
      rows.set(key, {
        key,
        projectId,
        taskTypeId,
        accountId: accountId || null,
        projectName: projects.find((p) => p.id === projectId)?.name || 'Unknown project',
        accountName: accountId ? accounts.find((a) => a.id === accountId)?.name || 'Unknown customer' : null,
        taskTypeName: taskType?.name || 'Unknown task',
        taskTypeCategory: taskType?.category || 'Billable',
        commentPolicy: taskType?.commentPolicy || 'NotAvailable',
        pinId: null,
        entriesByDate: {},
      });
    }
    return rows.get(key);
  };

  for (const pin of pinnedRows) {
    ensureRow(pin.projectId, pin.taskTypeId, pin.accountId).pinId = pin.id;
  }
  for (const entry of entries) {
    const row = ensureRow(entry.projectId, entry.taskTypeId, entry.accountId);
    row.entriesByDate[apiDateKey(entry.entryDate)] = entry;
  }

  return Array.from(rows.values()).sort(
    (a, b) =>
      a.projectName.localeCompare(b.projectName) ||
      (a.accountName || '').localeCompare(b.accountName || '') ||
      a.taskTypeName.localeCompare(b.taskTypeName),
  );
}

export function isEntryApproved(entry) {
  return !!(entry?.managerApprovedAt || entry?.pmApprovedAt);
}

export function isEntrySubmitted(entry) {
  return !!entry?.submittedAt;
}

// A reject clears submittedAt (back to draft) but leaves the reason fields set until the next
// submit-week clears them (apps/api time-entries.service.ts) -- so their mere presence means
// "rejected, not yet resubmitted."
export function rejectionReason(entry) {
  if (!entry) return null;
  if (entry.managerRejectionReason) return `Sent back by your manager: ${entry.managerRejectionReason}`;
  if (entry.pmRejectionReason) return `Sent back by the project PM: ${entry.pmRejectionReason}`;
  return null;
}

// Whether the visible week has anything left to submit, and/or anything submitted-but-unapproved
// left to recall -- both can be true at once (a partially-submitted week), which is why the UI
// offers both actions rather than one toggle.
export function weekSubmissionState(rows, dateKeys) {
  let hasUnsubmitted = false;
  let hasSubmitted = false;
  for (const row of rows) {
    for (const key of dateKeys) {
      const entry = row.entriesByDate[key];
      if (!entry || !entry.hours) continue;
      if (isEntrySubmitted(entry)) hasSubmitted = hasSubmitted || !isEntryApproved(entry);
      else hasUnsubmitted = true;
    }
  }
  return { hasUnsubmitted, hasSubmitted };
}

// Whether every entry in the visible week is submitted -- mirrors the server's
// TimesheetCommentsService.assertWeekOpen, which freezes comments only once the whole week (every
// row) is submitted. An EOM close often submits just one day while the rest of the week is still
// open, so any unsubmitted entry anywhere in the week keeps every row's comment editable.
export function weekFullySubmitted(rows, dateKeys) {
  let sawEntry = false;
  let sawOpen = false;
  for (const row of rows) {
    for (const key of dateKeys) {
      const entry = row.entriesByDate[key];
      if (!entry) continue;
      sawEntry = true;
      if (!isEntrySubmitted(entry)) sawOpen = true;
    }
  }
  return sawEntry && !sawOpen;
}

// A row freezes only once it's fully approved -- an approved entry AND nothing still open in it
// (mirrors TimesheetCommentsService.assertWeekOpen). An approved day next to a still-open one (an
// EOM close approves Monday while Tuesday isn't logged yet) must NOT lock: there's live,
// unreviewed work in the same row and the comment needs to keep describing it.
export function rowCommentLocked(row, dateKeys) {
  let hasApproved = false;
  let hasOpen = false;
  for (const key of dateKeys) {
    const entry = row.entriesByDate[key];
    if (!entry) continue;
    if (isEntryApproved(entry)) hasApproved = true;
    else if (!isEntrySubmitted(entry)) hasOpen = true;
  }
  return hasApproved && !hasOpen;
}

// A row whose task type Requires a comment and has hours this week but no comment yet. The server
// refuses to submit the week until this list is empty (TimeEntriesService.assertRequiredCommentsPresent)
// -- surfaced here so the UI says so before the button is pressed rather than after.
export function rowsMissingRequiredComment(rows, dateKeys, commentsByRow = {}) {
  return rows.filter(
    (row) =>
      row.commentPolicy === 'Required' &&
      weekRowTotal(row, dateKeys) > 0 &&
      !(commentsByRow[row.key] || '').trim(),
  );
}

// Rows arrive already sorted (project, customer, task type) from buildMyTimeRows -- this just
// collapses consecutive rows into one group so the grid can merge the project cell instead of
// repeating it (Section 5 item #18). Grouped by project AND account: two customers' non-project
// Travel are both under Administrative Time but are different work and get their own merged cell.
export function groupRowsByProject(rows) {
  const groups = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.projectId === row.projectId && last.accountId === (row.accountId || null)) {
      last.rows.push(row);
    } else {
      groups.push({
        projectId: row.projectId,
        projectName: row.projectName,
        accountId: row.accountId || null,
        accountName: row.accountName || null,
        rows: [row],
      });
    }
  }
  return groups;
}

// Sorting a grouped grid, not a flat one (Jeremy, 2026-08-17): the Project cell is a rowSpan across
// every task in that project, so a global sort by task would shatter the merge. Instead each key
// sorts the level it belongs to -- 'project' reorders the groups, 'task' reorders the rows inside
// each group -- which keeps the merged cell intact whichever column is active.
// Default is { project, asc }, exactly the order buildMyTimeRows already returns.
// Local rather than imported from Configuration.js, which has its own copy: that one isn't exported,
// a page-to-page import would be worse than a few lines here, and CLAUDE.md rules out standing up a
// shared component layer without a decision doc. The `.sortable`/`.sort-arrow` CSS is already global
// in App.css, so both grids look identical.
function SortTh({ label, sortKey, sort, onSort, style, title }) {
  const active = sort.key === sortKey;
  return (
    <th className="sortable" style={style} title={title} onClick={() => onSort(sortKey)}>
      {label}
      <span className={`sort-arrow${active ? '' : ' is-idle'}`}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

export function sortMyTimeGroups(groups, sort) {
  const dir = sort.dir === 'desc' ? -1 : 1;
  if (sort.key === 'task') {
    // Groups keep their project order; only the rows within a project move.
    return groups.map((g) => ({
      ...g,
      rows: [...g.rows].sort((a, b) => dir * a.taskTypeName.localeCompare(b.taskTypeName)),
    }));
  }
  // Account is the tiebreak for the same reason buildMyTimeRows uses it: Travel for two customers
  // is two groups on the same project, and they'd otherwise swap places between renders.
  return [...groups].sort(
    (a, b) =>
      dir * (a.projectName.localeCompare(b.projectName) || (a.accountName || '').localeCompare(b.accountName || '')),
  );
}

// First and last calendar day of the month `date` falls in -- used for the planned-vs-logged
// summary, which is monthly (matching ResourceMonthlyAllocation) even though the grid above it is
// weekly (ADR 0022 vs. ADR 0023 operate at different granularities; this reconciles at month level
// rather than forcing allocations into the weekly grid).
export function monthBounds(date) {
  return {
    first: new Date(date.getFullYear(), date.getMonth(), 1),
    last: new Date(date.getFullYear(), date.getMonth() + 1, 0),
  };
}

export function plannedHoursForMonth(assignments, year, month) {
  return round2(
    assignments.reduce(
      (sum, a) =>
        sum +
        (a.monthlyAllocations || [])
          .filter((c) => c.year === year && c.month === month)
          .reduce((s, c) => s + (c.plannedHours || 0), 0),
      0,
    ),
  );
}

export function weekRowTotal(row, dateKeys) {
  return round2(dateKeys.reduce((sum, key) => sum + (row.entriesByDate[key]?.hours || 0), 0));
}

export function weekGrandTotal(rows, dateKeys) {
  return round2(rows.reduce((sum, row) => sum + weekRowTotal(row, dateKeys), 0));
}

// One sum per day across every row passed in -- used for the "Total" row at the bottom of the grid.
export function dayTotals(rows, dateKeys) {
  return Object.fromEntries(
    dateKeys.map((key) => [
      key,
      round2(rows.reduce((sum, row) => sum + (row.entriesByDate[key]?.hours || 0), 0)),
    ]),
  );
}

// Week total broken out by Billable Category (Jeremy, 2026-08-05) -- keyed by category value
// (see TASK_TYPE_CATEGORIES), only the categories actually present.
export function weekTotalsByCategory(rows, dateKeys) {
  const byCategory = {};
  for (const row of rows) {
    const category = row.taskTypeCategory || 'Billable';
    byCategory[category] = round2((byCategory[category] || 0) + weekRowTotal(row, dateKeys));
  }
  return byCategory;
}

// The recap's two utilization rates (Jeremy, 2026-08-12). Denominator is the hours actually logged
// this week minus time off -- deliberately not a 40h standard week, since nothing in the data model
// carries a weekly standard and approved leave shouldn't drag the rate down. So these read as "how
// the week you worked was spent", and a short week still reads 100% if all of it was billable.
// Leave is identified by its own `TimeOff` category (Jeremy, 2026-08-12), replacing a hardcoded set
// of task-type names -- Configuration is now the only place that decides what counts as leave.
export function weekUtilization(rows, dateKeys) {
  let billable = 0;
  let productive = 0;
  let available = 0;
  for (const row of rows) {
    const hours = weekRowTotal(row, dateKeys);
    if (!hours || row.taskTypeCategory === TIME_OFF_CATEGORY) continue;
    available += hours;
    if (row.taskTypeCategory === 'Billable') billable += hours;
    if (row.taskTypeCategory === 'Billable' || row.taskTypeCategory === 'NonBillableProductive') {
      productive += hours;
    }
  }
  // Nothing to divide by: an empty week, or one that's entirely time off. Null so the card can show
  // an em dash -- 0% would read as "you were 0% billable", which isn't what an empty week means.
  const pct = (n) => (available > 0 ? Math.round((n / available) * 1000) / 10 : null);
  return {
    availableHours: round2(available),
    billableHours: round2(billable),
    productiveHours: round2(productive),
    billablePct: pct(billable),
    productivePct: pct(productive),
  };
}

// Planned-this-month broken out per project (item 3, 2026-08-05) instead of one lump sum -- lets a
// PM see at a glance which project is eating the month's forecasted hours. Zero-hour projects are
// dropped rather than shown as noise.
export function plannedHoursByProject(assignments, projects, year, month) {
  const byProject = new Map();
  for (const a of assignments) {
    const hours = (a.monthlyAllocations || [])
      .filter((c) => c.year === year && c.month === month)
      .reduce((s, c) => s + (c.plannedHours || 0), 0);
    if (!hours) continue;
    byProject.set(a.projectId, (byProject.get(a.projectId) || 0) + hours);
  }
  return Array.from(byProject.entries())
    .map(([projectId, hours]) => ({
      projectId,
      projectName: projects.find((p) => p.id === projectId)?.name || 'Unknown project',
      hours: round2(hours),
    }))
    .sort((a, b) => b.hours - a.hours);
}

// Logged-this-month broken out per project (Jeremy, 2026-08-05), same shape as
// plannedHoursByProject. Billable-only (Jeremy, 2026-08-05, revised) -- a PM watching this wants
// delivery hours against the month's forecast, not PTO/internal time inflating the number, so this
// excludes the Administrative Time project outright (regardless of category -- a couple of its own
// task types are themselves categorized Billable, e.g. Post Go Live Support) and, for real
// projects, any task type whose category isn't 'Billable'.
export function loggedHoursByProject(monthEntries, projects, taskTypes) {
  const byProject = new Map();
  for (const entry of monthEntries) {
    const project = projects.find((p) => p.id === entry.projectId);
    if (project?.isAdministrative) continue;
    const taskType = taskTypes.find((t) => t.id === entry.taskTypeId);
    if ((taskType?.category || 'Billable') !== 'Billable') continue;
    byProject.set(entry.projectId, (byProject.get(entry.projectId) || 0) + entry.hours);
  }
  return Array.from(byProject.entries())
    .map(([projectId, hours]) => ({
      projectId,
      projectName: projects.find((p) => p.id === projectId)?.name || 'Unknown project',
      hours: round2(hours),
    }))
    .sort((a, b) => b.hours - a.hours);
}

// One monthly table (Jeremy, 2026-08-11) instead of two side-by-side lists: Project / FCST / Logged /
// Remaining. Outer-joined on project so a project with forecast but no hours (and vice versa) still
// gets a line -- the gap is exactly what a PM is looking for. Remaining can go negative, which is
// real information (over the forecast), so it isn't floored at zero. Sorted by project name (Jeremy,
// 2026-08-11) -- a stable A-Z beats hours-descending for finding a known project week to week.
export function monthlyProjectSummary(plannedByProject, loggedByProject) {
  const byId = new Map();
  const ensure = (projectId, projectName) => {
    if (!byId.has(projectId)) byId.set(projectId, { projectId, projectName, fcst: 0, logged: 0 });
    return byId.get(projectId);
  };
  for (const p of plannedByProject) ensure(p.projectId, p.projectName).fcst = p.hours;
  for (const p of loggedByProject) ensure(p.projectId, p.projectName).logged = p.hours;

  const rows = Array.from(byId.values())
    .map((r) => ({ ...r, remaining: round2(r.fcst - r.logged) }))
    .sort((a, b) => a.projectName.localeCompare(b.projectName));
  return {
    rows,
    total: {
      fcst: round2(rows.reduce((s, r) => s + r.fcst, 0)),
      logged: round2(rows.reduce((s, r) => s + r.logged, 0)),
      remaining: round2(rows.reduce((s, r) => s + r.remaining, 0)),
    },
  };
}

// Single source for both an hour cell's shading and the legend under the grid (Jeremy, 2026-08-11) --
// a legend kept separately from the styles it explains goes wrong the first time a color changes.
// Order matters: entryCellState returns the first match, so a locked state wins over `saving`.
export const CELL_STATES = [
  { key: 'approved', label: 'Approved', background: 'rgba(0,124,100,0.12)', border: '1px solid transparent' },
  { key: 'submitted', label: 'Submitted, awaiting approval', background: 'rgba(251,176,64,0.14)', border: '1px solid transparent' },
  { key: 'rejected', label: 'Sent back — fix and resubmit', background: 'rgba(239,65,54,0.08)', border: '1px solid var(--red)' },
  { key: 'closed', label: 'Fiscal period archived — closed', background: 'rgba(68,84,106,0.10)', border: '1px solid transparent' },
  { key: 'saving', label: 'Saving…', background: 'rgba(47,147,177,0.10)', border: '1px solid transparent' },
  { key: 'draft', label: 'Draft — not submitted', background: 'transparent', border: '1px solid transparent' },
];

export function entryCellState(entry, { closed = false, saving = false } = {}) {
  if (isEntryApproved(entry)) return 'approved';
  if (isEntrySubmitted(entry)) return 'submitted';
  if (rejectionReason(entry)) return 'rejected';
  if (closed) return 'closed';
  if (saving) return 'saving';
  return 'draft';
}

function cellStyleFor(stateKey) {
  return CELL_STATES.find((s) => s.key === stateKey) || CELL_STATES[CELL_STATES.length - 1];
}

function CellLegend() {
  return (
    <div
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
        marginTop: 10, fontSize: 11, color: 'var(--text-mid)',
      }}
    >
      {CELL_STATES.map((s) => (
        <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 16, height: 12, borderRadius: 2, background: s.background,
              // A transparent cell border is right in the grid but invisible in a swatch.
              border: s.border === '1px solid transparent' ? '1px solid var(--border)' : s.border,
              display: 'inline-block',
            }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// Two overlapping conversation bubbles. Inline SVG rather than an emoji so it takes currentColor
// (red when a required comment is missing) and renders identically across platforms.
function CommentIcon({ filled }) {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M2.5 3.5h10a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H7l-3 2.5V12H2.5A1.5 1.5 0 0 1 1 10.5v-5A1.5 1.5 0 0 1 2.5 3.5Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M16 7h1.5A1.5 1.5 0 0 1 19 8.5v5a1.5 1.5 0 0 1-1.5 1.5H16v2l-2.5-2h-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div
      style={{
        padding: '12px 16px', borderRadius: 8, marginBottom: 20,
        background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
        fontSize: 13, color: 'var(--red)',
      }}
    >
      ⚠ {error}
    </div>
  );
}

// Up/Down on a number input steps the value, which fights the grid's up/down-to-move-rows muscle
// memory (Excel/Sheets style). Matched by day key rather than DOM position: the project cell's
// rowSpan means a group's first <tr> has one more <td> than the rest, so a fixed child-index would
// land on the wrong day for every row after the first.
export function focusAdjacentDayCell(fromInput, direction, dayKey) {
  let tr = fromInput.closest('tr');
  while (tr) {
    tr = direction === -1 ? tr.previousElementSibling : tr.nextElementSibling;
    const target = tr && tr.querySelector(`input[data-day-key="${dayKey}"]`);
    if (target) {
      target.focus();
      return;
    }
  }
}

// Enter acts like Tab: focus moves to the next enabled day cell in reading order, which blurs the
// current one and so commits it. On the last cell there is nowhere to go, so just blur (commit).
export function focusNextDayCell(fromInput) {
  const cells = Array.from(
    (fromInput.closest('table') || document).querySelectorAll('input[data-day-key]:not(:disabled)')
  );
  const next = cells[cells.indexOf(fromInput) + 1];
  if (next) {
    next.focus();
    next.select(); // Tab selects the cell's contents; focus() alone would make typing append to "8".
  } else fromInput.blur();
}

// A single hour cell. Uncontrolled-while-focused (draft state) so typing doesn't fight the server
// round trip, and commits on blur — the friendliest timesheet interaction: click, type, tab away,
// it's saved. No separate "Save" button to remember to click.
function TimesheetCell({ entry, disabled, closed, saving, onCommit, dayKey }) {
  const persisted = entry ? String(entry.hours) : '';
  const [draft, setDraft] = useState(null);
  const value = draft ?? persisted;
  const state = entryCellState(entry, { closed, saving });
  const { background, border } = cellStyleFor(state);
  const rejected = state === 'rejected' && rejectionReason(entry);

  return (
    <input
      type="number"
      min="0"
      max="24"
      step="0.25"
      inputMode="decimal"
      disabled={disabled}
      value={value}
      data-day-key={dayKey}
      title={
        state === 'approved'
          ? 'Approved — ask a manager or PM to change this'
          : state === 'submitted'
            ? 'Submitted — unsubmit the week to change this'
            : rejected || undefined
      }
      style={{
        width: '100%',
        textAlign: 'center',
        border,
        background,
        font: 'inherit',
        fontSize: 13,
        padding: '9px 4px',
        borderRadius: 3,
      }}
      onFocus={() => setDraft(persisted)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const typed = draft;
        setDraft(null);
        if (typed !== null && typed !== persisted) onCommit(typed);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          focusNextDayCell(e.target);
          return;
        }
        // Arrow keys are navigation only here -- Up/Down move a row instead of stepping the value.
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        focusAdjacentDayCell(e.target, e.key === 'ArrowUp' ? -1 : 1, dayKey);
      }}
    />
  );
}

// SUPER user-friendly timesheet entry (Jeremy, 2026-08-03): one row per (project, task type), one
// column per day, type hours and tab away. Rows persist via pinned TimesheetRows so nobody rebuilds
// their timesheet from scratch every week.
function TimeEntry() {
  // permissions comes from the shared context (loaded once at sign-in) rather than a fetch here --
  // the nav needs the same answer, and three copies of one call was two too many.
  const { currentUser, permissions } = useApp();
  const [anchor, setAnchor] = useState(() => new Date());
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [pinnedRows, setPinnedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [copying, setCopying] = useState(false);
  const [copyNote, setCopyNote] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [newRowProjectId, setNewRowProjectId] = useState('');
  const [newRowTaskTypeId, setNewRowTaskTypeId] = useState('');
  const [newRowAccountId, setNewRowAccountId] = useState('');
  const [pinning, setPinning] = useState(false);
  // Per-row, per-week comments (2026-08-12): `comments` is what the server has, `commentDrafts`
  // what's been typed but not committed (saved on blur, same as an hours cell).
  const [comments, setComments] = useState([]);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [openCommentKey, setOpenCommentKey] = useState('');
  const [savingCommentKey, setSavingCommentKey] = useState('');
  const [submitAction, setSubmitAction] = useState(''); // '' | 'submit' | 'unsubmit'
  const [assignments, setAssignments] = useState([]);
  const [monthEntries, setMonthEntries] = useState([]);
  // Proxy time entry (2026-08-05): who this page is actually reading/writing for. '' means "myself"
  // -- resolved below to currentUser.id so the very first render (before proxyTargets has loaded)
  // still behaves exactly like the old self-only page.
  const [proxyTargets, setProxyTargets] = useState([]);
  const [activeUserId, setActiveUserId] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [fiscalWeeks, setFiscalWeeks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  // ?approvals=1 lands here from a timesheet notification, so the bell opens the queue itself
  // rather than dropping the approver on their own week.
  const [searchParams] = useSearchParams();
  const [showApprovals, setShowApprovals] = useState(searchParams.get('approvals') === '1');
  // Also as an effect, not just initial state: an approver already sitting on this page doesn't
  // remount when the bell navigates here, so the param would otherwise be ignored.
  useEffect(() => {
    if (searchParams.get('approvals') === '1') setShowApprovals(true);
  }, [searchParams]);

  const weekLabel = useMemo(() => fiscalWeekLabel(fiscalWeeks), [fiscalWeeks]);
  const days = useMemo(() => weekDates(anchor), [anchor]);
  const keys = useMemo(() => days.map(dateKey), [days]);
  const weekendKeys = useMemo(
    () => new Set(days.filter(isWeekendDate).map(dateKey)),
    [days],
  );
  // Grayed out the same as weekends (Configuration's "Company Holidays" tab is the editor) --
  // combined into one set here so the render logic below doesn't need to know there are two sources.
  const holidayKeys = useMemo(() => new Set(holidays.map((h) => apiDateKey(h.date))), [holidays]);
  const holidayNameByKey = useMemo(() => new Map(holidays.map((h) => [apiDateKey(h.date), h.name])), [holidays]);
  const nonWorkingKeys = useMemo(() => new Set([...weekendKeys, ...holidayKeys]), [weekendKeys, holidayKeys]);
  const closedKeys = useMemo(() => archivedDateKeys(fiscalWeeks, keys), [fiscalWeeks, keys]);
  const closedPeriodNames = useMemo(
    () => (fiscalWeeks || []).filter((w) => w.isArchived).map((w) => w.periodName).join(', '),
    [fiscalWeeks],
  );
  const monthYear = anchor.getFullYear();
  const monthNum = anchor.getMonth() + 1;
  const targetUserId = activeUserId || currentUser?.id;
  const isProxying = !!currentUser && targetUserId !== currentUser.id;
  const isAdmin = currentUser?.role === 'admin';
  // Decides whether the Approvals button exists at all -- only people who actually approve as a
  // manager and/or PM get it (admins always qualify, matching the approve/reject endpoints).
  const canApprove = isAdmin || !!permissions?.isManagerOfAnyone || !!permissions?.isProjectManagerOfAny;
  const activeProxyTarget = proxyTargets.find((t) => t.id === targetUserId);

  // Fiscal week label(s) for the visible week (ADR 0026) -- best-effort: a week with no FiscalWeek
  // row yet (too far in the future, or history not backfilled that far back) just shows no label
  // rather than blocking the timesheet on it.
  //
  // Asked for the whole 7-day range, not just day one (Jeremy, 2026-08-10): a fiscal week isn't
  // always 7 days -- Finance restarts each fiscal month on the 1st, so a month's last week stretches
  // (FY26-W21 is May 23-31). Labeling by the first day alone made the grid week of May 30 - Jun 5
  // read as a second "FY26-W21" when all five working days in it are actually FY26-W22.
  // Batched (Promise.all), not two independent effects -- same trigger (the visible week), same
  // convention as this component's own `load`/`loadMonthSummary` below.
  useEffect(() => {
    Promise.all([
      apiFetch(`/fiscal-weeks/for-range?start=${keys[0]}&end=${keys[6]}`).catch(() => []),
      apiFetch(`/company-holidays/for-range?start=${keys[0]}&end=${keys[6]}`).catch(() => []),
    ]).then(([weeks, holidayRows]) => {
      setFiscalWeeks(weeks || []);
      setHolidays(holidayRows || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys[0]]);

  // Who the signed-in person is allowed to enter time for besides themselves -- empty for almost
  // everyone, which is what keeps the switcher below hidden for anyone who isn't a manager/admin.
  useEffect(() => {
    if (!currentUser) return;
    apiFetch('/time-entries/proxy-targets').then(setProxyTargets).catch(() => setProxyTargets([]));
  }, [currentUser]);

  // Badges the Approvals button below -- no date range, so it's a true count of everything still
  // sitting in this person's queue (as manager and/or PM), not just the visible week. Re-run after
  // every approve/reject (ApprovalsTab calls back): a fetch-once badge outlived the queue it
  // describes and kept claiming work that had just been signed off.
  const refreshApprovalCount = useCallback(() => {
    if (!currentUser) return;
    apiFetch('/time-entries/pending-approvals')
      .then((result) => setPendingApprovalCount(countPendingTimesheets(result)))
      .catch(() => setPendingApprovalCount(0));
  }, [currentUser]);

  useEffect(() => { refreshApprovalCount(); }, [refreshApprovalCount]);

  const load = useCallback(async () => {
    if (!currentUser || !targetUserId) return;
    setLoading(true);
    setError('');
    try {
      const [entryData, projectData, taskTypeData, pinnedData, accountData, commentData] = await Promise.all([
        apiFetch(`/time-entries?userId=${targetUserId}&from=${keys[0]}&to=${keys[6]}`),
        apiFetch('/projects'),
        // Inactive types included so a row logged against a retired type still shows its real name
        // instead of "Unknown task" (Jeremy, 2026-08-13) -- the OnePlan import creates unmatched
        // task names as inactive, and that was most of the imported history. `availableTaskTypes`
        // filters back down to active for the Add-row picker, so nothing retired can be chosen.
        apiFetch('/task-types?includeInactive=true'),
        apiFetch(isProxying ? `/timesheet-rows?onBehalfOfUserId=${targetUserId}` : '/timesheet-rows'),
        apiFetch('/accounts'),
        apiFetch(
          `/timesheet-comments?weekStart=${keys[0]}${isProxying ? `&onBehalfOfUserId=${targetUserId}` : ''}`,
        ),
      ]);
      setEntries(entryData);
      setProjects(projectData);
      setTaskTypes(taskTypeData);
      setPinnedRows(pinnedData);
      setAccounts(accountData);
      setComments(commentData);
      setCommentDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // keys[0]/keys[6] change together whenever `anchor` does, so depending on the week's bounds
    // (not the whole `days`/`keys` array identity) is enough and avoids re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, targetUserId, isProxying, keys[0], keys[6]]);

  useEffect(() => { load(); }, [load]);

  // Best-effort planned-vs-logged summary (item 3) -- monthly, independent of the weekly grid
  // above, so a failure here (e.g. no assignments yet) shouldn't block the timesheet itself.
  const loadMonthSummary = useCallback(async () => {
    if (!currentUser || !targetUserId) return;
    try {
      const { first, last } = monthBounds(anchor);
      const [assignmentData, monthEntryData] = await Promise.all([
        apiFetch(`/resource-assignments?userId=${targetUserId}`),
        apiFetch(`/time-entries?userId=${targetUserId}&from=${dateKey(first)}&to=${dateKey(last)}`),
      ]);
      setAssignments(assignmentData);
      setMonthEntries(monthEntryData);
    } catch {
      setAssignments([]);
      setMonthEntries([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, targetUserId, monthYear, monthNum]);

  useEffect(() => { loadMonthSummary(); }, [loadMonthSummary]);

  const rows = useMemo(
    () => buildMyTimeRows(entries, pinnedRows, projects, taskTypes, accounts),
    [entries, pinnedRows, projects, taskTypes, accounts],
  );
  // Defaults to the order buildMyTimeRows already returns, so the grid opens unchanged.
  const [sort, setSort] = useState({ key: 'project', dir: 'asc' });
  const toggleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }, []);
  const groups = useMemo(() => sortMyTimeGroups(groupRowsByProject(rows), sort), [rows, sort]);
  const { hasUnsubmitted, hasSubmitted } = weekSubmissionState(rows, keys);
  // A locked month never freezes comments (the API agrees); submission/approval freezes still apply to open weeks.
  const periodClosed = closedKeys.size > 0;
  const commentsLocked = !periodClosed && weekFullySubmitted(rows, keys);
  const commentsByRow = useMemo(() => {
    const byRow = {};
    for (const c of comments) byRow[rowKey(c.projectId, c.taskTypeId, c.accountId)] = c.comment;
    return byRow;
  }, [comments]);
  const missingComments = useMemo(
    () => rowsMissingRequiredComment(rows, keys, commentsByRow),
    [rows, keys, commentsByRow],
  );
  const plannedByProject = useMemo(
    () => plannedHoursByProject(assignments, projects, monthYear, monthNum),
    [assignments, projects, monthYear, monthNum],
  );
  const loggedByProject = useMemo(
    () => loggedHoursByProject(monthEntries, projects, taskTypes),
    [monthEntries, projects, taskTypes],
  );
  const monthSummary = useMemo(
    () => monthlyProjectSummary(plannedByProject, loggedByProject),
    [plannedByProject, loggedByProject],
  );
  const weekByCategory = useMemo(() => weekTotalsByCategory(rows, keys), [rows, keys]);
  const weekUtil = useMemo(() => weekUtilization(rows, keys), [rows, keys]);

  // Task types already paired with the selected project don't need offering again -- except an
  // account-based one, which is legitimately on the timesheet once per customer.
  const selectedProjectIsAdministrative = !!projects.find((p) => p.id === newRowProjectId)?.isAdministrative;
  // Sorted A-Z here rather than left in the API's order (Jeremy, 2026-08-12) -- `sortOrder` groups
  // the admin types in the 100s and is null on the rest, so the list arrived effectively unordered.
  const availableTaskTypes = useMemo(
    () =>
      taskTypes
        .filter(
          (t) =>
            // The list now carries inactive types for display; only active ones can be added.
            t.isActive &&
            isTaskTypeAvailableForProject(t, selectedProjectIsAdministrative, newRowProjectId) &&
            (taskTypeNeedsAccount(t, selectedProjectIsAdministrative) ||
              !rows.some((r) => r.projectId === newRowProjectId && r.taskTypeId === t.id)),
        )
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [taskTypes, rows, newRowProjectId, selectedProjectIsAdministrative],
  );
  const newRowTaskType = taskTypes.find((t) => t.id === newRowTaskTypeId);
  const newRowNeedsAccount = taskTypeNeedsAccount(newRowTaskType, selectedProjectIsAdministrative);
  // Same customer + task already on the timesheet -- the server would just hand back the existing pin.
  const newRowAlreadyPinned =
    !!newRowAccountId &&
    rows.some(
      (r) => r.projectId === newRowProjectId && r.taskTypeId === newRowTaskTypeId && r.accountId === newRowAccountId,
    );

  // Patches `entries` in place from the API response instead of calling `load()` -- a full reload
  // sets `loading` true and swaps the whole grid out for "Loading…" on every single cell blur,
  // which reads as the page refreshing. Nothing else (projects/taskTypes/pinnedRows) changes from
  // editing an hour, so there's nothing else worth refetching here.
  async function saveCell(row, day, existingEntry, typedValue) {
    const cellKey = `${row.key}|${dateKey(day)}`;
    const hours = typedValue === '' ? null : Number(typedValue);
    if (hours != null && !Number.isFinite(hours)) return;
    if (existingEntry && hours === existingEntry.hours) return;
    if (!existingEntry && !hours) return;

    setSavingKey(cellKey);
    setError('');
    try {
      if (existingEntry && !hours) {
        await apiFetch(`/time-entries/${existingEntry.id}`, { method: 'DELETE' });
        setEntries((prev) => prev.filter((e) => e.id !== existingEntry.id));
      } else if (existingEntry) {
        const updated = await apiFetch(`/time-entries/${existingEntry.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ hours }),
        });
        setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        const created = await apiFetch('/time-entries', {
          method: 'POST',
          body: JSON.stringify({
            projectId: row.projectId,
            taskTypeId: row.taskTypeId,
            ...(row.accountId ? { accountId: row.accountId } : {}),
            hours,
            entryDate: dateKey(day),
            ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}),
          }),
        });
        setEntries((prev) => [...prev, created]);
      }
      // Best-effort and silent (no loading flag) -- keeps "Planned/Logged this month" honest
      // without another full-grid reload.
      loadMonthSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey('');
    }
  }

  // Saved on blur like an hours cell. Patches `comments` in place rather than reloading the grid,
  // same reasoning as saveCell.
  async function copyLastWeek() {
    setCopying(true);
    setError('');
    setCopyNote('');
    try {
      const shift = (d) => dateKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7));
      const prev = await apiFetch(`/time-entries?userId=${targetUserId}&from=${shift(days[0])}&to=${shift(days[6])}`);
      const { toCreate, skipped } = planCopyForward(prev, entries, { closedKeys, holidayKeys });
      for (const body of toCreate) {
        const created = await apiFetch('/time-entries', {
          method: 'POST',
          body: JSON.stringify({ ...body, ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}) }),
        });
        setEntries((p) => [...p, created]);
      }
      setCopyNote(
        toCreate.length
          ? `Copied ${toCreate.length} entr${toCreate.length === 1 ? 'y' : 'ies'} from last week${skipped ? ` (${skipped} skipped)` : ''}.`
          : 'Nothing to copy: last week is empty or this week already has those entries.',
      );
      loadMonthSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(false);
    }
  }

  async function saveComment(row, value) {
    const current = commentsByRow[row.key] || '';
    if (value.trim() === current.trim()) {
      setCommentDrafts((prev) => ({ ...prev, [row.key]: undefined }));
      return;
    }
    setSavingCommentKey(row.key);
    setError('');
    try {
      await apiFetch('/timesheet-comments', {
        method: 'POST',
        body: JSON.stringify({
          projectId: row.projectId,
          taskTypeId: row.taskTypeId,
          ...(row.accountId ? { accountId: row.accountId } : {}),
          weekStart: keys[0],
          comment: value,
          ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}),
        }),
      });
      setComments((prev) => {
        const others = prev.filter((c) => rowKey(c.projectId, c.taskTypeId, c.accountId) !== row.key);
        return value.trim()
          ? [...others, { projectId: row.projectId, taskTypeId: row.taskTypeId, accountId: row.accountId, comment: value.trim() }]
          : others;
      });
      setCommentDrafts((prev) => ({ ...prev, [row.key]: undefined }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingCommentKey('');
    }
  }

  // Batch submit/recall over the visible week (item 1) -- scoped by date range, not by row, so it
  // covers everything currently on screen in one action.
  async function submitWeek() {
    setSubmitAction('submit');
    setError('');
    try {
      await apiFetch('/time-entries/submit-week', {
        method: 'POST',
        body: JSON.stringify({ from: keys[0], to: keys[6], ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}) }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitAction('');
    }
  }

  async function unsubmitWeek() {
    setSubmitAction('unsubmit');
    setError('');
    try {
      await apiFetch('/time-entries/unsubmit-week', {
        method: 'POST',
        body: JSON.stringify({ from: keys[0], to: keys[6], ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}) }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitAction('');
    }
  }

  async function pinRow(e) {
    e.preventDefault();
    if (!newRowProjectId || !newRowTaskTypeId) return;
    if (newRowNeedsAccount && !newRowAccountId) return;
    setPinning(true);
    setError('');
    try {
      await apiFetch('/timesheet-rows', {
        method: 'POST',
        body: JSON.stringify({
          projectId: newRowProjectId,
          taskTypeId: newRowTaskTypeId,
          ...(newRowNeedsAccount ? { accountId: newRowAccountId } : {}),
          ...(isProxying ? { onBehalfOfUserId: targetUserId } : {}),
        }),
      });
      setNewRowProjectId('');
      setNewRowTaskTypeId('');
      setNewRowAccountId('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setPinning(false);
    }
  }

  async function unpinRow(row) {
    if (!row.pinId) return;
    setError('');
    try {
      await apiFetch(`/timesheet-rows/${row.pinId}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function shiftWeek(deltaWeeks) {
    setCopyNote('');
    setAnchor((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
  }

  const grandTotal = weekGrandTotal(rows, keys);
  const dayTotalsByKey = dayTotals(rows, keys);

  if (!RESOURCES_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">⏱️</div>
        <div className="placeholder-title">My Time</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set{' '}
          <code>REACT_APP_RESOURCES_ENABLED=true</code> to enable it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Resourcing</div>
        <h1 className="page-title">My Time</h1>
        <p className="page-subtitle">Log your hours — pick a project and task, type hours, done.</p>
      </div>

      {(proxyTargets.length > 0 || canApprove) && (
        <div
          className="card"
          style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
        >
          {proxyTargets.length > 0 ? (
            <div className="form-group" style={{ marginBottom: 0, minWidth: 240, maxWidth: 320 }}>
              {/* Same switcher drives both views, so it says what it does in the one you're on. */}
              <label className="form-label">{showApprovals ? 'Approving as' : 'Entering time for'}</label>
              <select
                className="form-select"
                value={activeUserId}
                onChange={(e) => setActiveUserId(e.target.value)}
              >
                <option value="">Myself</option>
                {proxyTargets.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.teamName ? ` (${t.teamName})` : ''}</option>
                ))}
              </select>
            </div>
          ) : <span />}
          {canApprove && (
            <button
              className={showApprovals ? 'btn btn-outline' : 'btn btn-primary'}
              onClick={() => setShowApprovals((v) => !v)}
              title={
                showApprovals || !pendingApprovalCount
                  ? undefined
                  : `${pendingApprovalCount} timesheet${pendingApprovalCount === 1 ? '' : 's'} awaiting your approval`
              }
            >
              {showApprovals
                ? '← Back to my timesheet'
                : `Approvals${pendingApprovalCount ? ` (${pendingApprovalCount})` : ''}`}
            </button>
          )}
        </div>
      )}

      {showApprovals ? (
        <ApprovalsTab
          permissions={permissions}
          isAdmin={isAdmin}
          onQueueChanged={refreshApprovalCount}
          onBehalfOfUserId={isProxying ? targetUserId : ''}
          approver={isProxying ? activeProxyTarget : null}
        />
      ) : (<>

      {isProxying && (
        <div className="proxy-banner" style={{ marginBottom: 16 }}>
          You are entering time for <strong>{activeProxyTarget?.name || 'this person'}</strong>. Hours
          save to their timesheet, not yours.
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn-outline" onClick={() => shiftWeek(-1)}>← Prev week</button>
            <div style={{ minWidth: 190, textAlign: 'center' }}>
              {weekLabel && (
                <div
                  style={{ fontSize: 11, color: weekLabel.splitsMonth ? 'var(--warn-text)' : 'var(--text-mid)' }}
                  title={weekLabel.splitsMonth ? 'This week crosses a fiscal month — its hours are reported under two different months.' : undefined}
                >
                  {weekLabel.text}
                </div>
              )}
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {days[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' – '}
                {days[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <button className="btn btn-outline" onClick={() => shiftWeek(1)}>Next week →</button>
            <button className="btn btn-outline" onClick={() => setAnchor(new Date())}>This week</button>
            <button
              className="btn btn-outline"
              disabled={copying || loading || weekFullySubmitted(rows, keys)}
              onClick={copyLastWeek}
              title="Copy last week's hours into this week's empty cells"
            >
              {copying ? 'Copying…' : 'Copy last week'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {/* Submit/unsubmit act on the whole visible range, so one closed day in it is enough for
                the API to refuse the request -- don't offer a button that can only fail. */}
            {hasUnsubmitted && (
              <button
                className="btn btn-primary"
                disabled={!!submitAction || closedKeys.size > 0 || missingComments.length > 0}
                onClick={submitWeek}
                title={
                  missingComments.length > 0
                    ? `Add a comment for ${missingComments.map((r) => r.taskTypeName).join(', ')} first`
                    : undefined
                }
              >
                {submitAction === 'submit' ? 'Submitting…' : 'Submit week'}
              </button>
            )}
            {hasSubmitted && (
              <button className="btn btn-outline" disabled={!!submitAction || closedKeys.size > 0} onClick={unsubmitWeek}>
                {submitAction === 'unsubmit' ? 'Unsubmitting…' : 'Unsubmit week'}
              </button>
            )}
          </div>
        </div>
        {/* Blocking, not advisory: the server refuses the whole week (it's one updateMany), so
            nothing submits until every one of these has a comment -- say that plainly rather than
            leaving a disabled button to explain itself. */}
        {missingComments.length > 0 && (
          <div
            style={{
              marginTop: 12, padding: '12px 16px', borderRadius: 8,
              background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
              fontSize: 13, color: 'var(--red)',
            }}
          >
            <div style={{ fontWeight: 600 }}>
              ⚠ This week can't be submitted until {missingComments.length === 1 ? 'this row has' : 'these rows have'} a comment
            </div>
            <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
              {missingComments.map((r) => (
                <li key={r.key}>
                  {r.accountName || r.projectName} — <strong>{r.taskTypeName}</strong>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 6, color: 'var(--text-mid)' }}>
              Nothing is submitted until all of them are filled in — use the bubble in the Notes
              column to say what the time was spent on.
            </div>
          </div>
        )}
        {copyNote && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-mid)' }}>{copyNote}</div>}
        {closedKeys.size > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--warn-text)' }}>
            {closedKeys.size === keys.length
              ? `Fiscal period ${closedPeriodNames} is archived — this week is closed to time entry.`
              : `Fiscal period ${closedPeriodNames} is archived — the grayed-out days in this week are closed to time entry.`}
          </div>
        )}
      </div>

      {/* Two cards, not two columns of one: the month roll-up and the week recap answer different
          questions and are read separately. They wrap to stacked cards on a narrow viewport. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="card" style={{ flex: '1 1 420px', minWidth: 300, marginBottom: 0, fontSize: 12, color: 'var(--text-mid)' }}>
          <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: 6 }}>
            {anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} — Billable
          </div>
          {monthSummary.rows.length === 0 ? (
            <span>No FCST or billable hours this month</span>
          ) : (
            <table className="mini-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th className="num">FCST</th>
                  <th className="num">Logged</th>
                  <th className="num">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {monthSummary.rows.map((r) => (
                  <tr key={r.projectId}>
                    <td>{r.projectName}</td>
                    <td className="num">{r.fcst}</td>
                    <td className="num">{r.logged}</td>
                    {/* Over the forecast is the one number worth coloring -- it's the exception. */}
                    <td className="num" style={r.remaining < 0 ? { color: 'var(--red)' } : undefined}>{r.remaining}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num">{monthSummary.total.fcst}</td>
                  <td className="num">{monthSummary.total.logged}</td>
                  <td className="num">{monthSummary.total.remaining}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="card" style={{ flex: '0 1 280px', minWidth: 240, marginBottom: 0, fontSize: 12, color: 'var(--text-mid)' }}>
          <div style={{ fontWeight: 600, color: 'var(--text-dark)', marginBottom: 6 }}>This Week Recap</div>
          <table className="mini-table">
            <tbody>
              {TASK_TYPE_CATEGORIES.map((c) => (
                <tr key={c.value}>
                  <td>{c.label}</td>
                  <td className="num">{weekByCategory[c.value] || 0}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{grandTotal}</td>
              </tr>
              {/* Utilization sits under the Total on purpose -- it's derived from those hours, and the
                  title spells out the denominator so a 100% short week doesn't read as a bug. */}
              <tr title={`Billable hours ÷ ${weekUtil.availableHours} hours logged this week, excluding time off`}>
                <td style={{ borderTop: '1px solid var(--border)' }}>Billable utilization</td>
                <td className="num" style={{ borderTop: '1px solid var(--border)' }}>
                  {weekUtil.billablePct == null ? '—' : `${weekUtil.billablePct}%`}
                </td>
              </tr>
              <tr title={`Billable + Non-Billable Productive hours ÷ ${weekUtil.availableHours} hours logged this week, excluding time off`}>
                <td>Productive utilization</td>
                <td className="num">
                  {weekUtil.productivePct == null ? '—' : `${weekUtil.productivePct}%`}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <SortTh
                  label="Project" sortKey="project" sort={sort} onSort={toggleSort}
                  style={{ textAlign: 'left', minWidth: 180 }}
                  title="Sort the projects on the grid"
                />
                <SortTh
                  label="Task" sortKey="task" sort={sort} onSort={toggleSort}
                  style={{ textAlign: 'left', minWidth: 160 }}
                  title="Sort the tasks within each project"
                />
                <th style={{ textAlign: 'left', minWidth: 150 }}>Billable Category</th>
                <th style={{ textAlign: 'center', width: 52 }} title="Add a note for this week saying what the time was spent on">
                  Notes
                </th>
                {days.map((d, i) => (
                  <th
                    key={keys[i]}
                    className={nonWorkingKeys.has(keys[i]) ? 'is-weekend' : undefined}
                    title={holidayNameByKey.get(keys[i])}
                    style={{ textAlign: 'center', width: 64, borderLeft: '1px solid var(--border)' }}
                  >
                    {DAY_NAMES[i]}
                    <br />
                    <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-mid)' }}>{d.getDate()}</span>
                  </th>
                ))}
                <th style={{ textAlign: 'center', width: 64, borderLeft: '1px solid var(--border)' }}>Total</th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {groups.map((group, gi) => {
                // An open comment editor is its own <tr>, so the merged project cell has to span it too.
                const openInGroup = group.rows.filter((r) => openCommentKey === r.key).length;
                return group.rows.map((row, i) => {
                  const commentOpen = openCommentKey === row.key;
                  const savedComment = commentsByRow[row.key] || '';
                  const commentValue =
                    commentDrafts[row.key] !== undefined ? commentDrafts[row.key] : savedComment;
                  const commentMissing = missingComments.includes(row);
                  const commentLocked = !periodClosed && (rowCommentLocked(row, keys) || commentsLocked);
                  return (
                  <React.Fragment key={row.key}>
                  <tr className={gi % 2 === 1 ? 'is-alt' : ''}>
                    {i === 0 && (
                      <td rowSpan={group.rows.length + openInGroup} style={{ padding: '8px 10px', fontWeight: 700 }}>
                        <div>{group.accountName || group.projectName}</div>
                        {/* Non-project rows: the customer is the headline, the Administrative Time
                            project underneath it is just where the hours technically live. */}
                        {group.accountName && (
                          <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-mid)' }}>{group.projectName}</div>
                        )}
                        {group.rows.length > 1 && (
                          <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-mid)', marginTop: 4 }}>
                            {weekGrandTotal(group.rows, keys)} hrs
                          </div>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '8px 10px', color: 'var(--text-mid)' }}>
                      {row.taskTypeName}
                      {/* Only account-based task types carry one (Travel, Post Go Live Support) --
                          for everything else the project column already names the customer. */}
                      {row.accountName && (
                        <div style={{ fontSize: 11, marginTop: 2, color: 'var(--text-light)' }}>
                          {row.accountName}
                        </div>
                      )}
                      {!commentOpen && (savedComment || commentMissing) && (
                        <div
                          style={{
                            fontSize: 11,
                            marginTop: 2,
                            color: commentMissing ? 'var(--warn-text)' : 'var(--text-mid)',
                            fontStyle: 'italic',
                          }}
                        >
                          {savedComment || 'Comment required'}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-mid)' }}>
                      {TASK_TYPE_CATEGORIES.find((c) => c.value === row.taskTypeCategory)?.label || row.taskTypeCategory}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {row.commentPolicy !== 'NotAvailable' && (
                        <button
                          title={
                            commentMissing
                              ? 'Comment required — what was this time spent on?'
                              : savedComment || 'Add a comment for this week'
                          }
                          aria-label={savedComment ? 'Edit this week’s comment' : 'Add a comment for this week'}
                          onClick={() => setOpenCommentKey(commentOpen ? '' : row.key)}
                          style={{
                            border: 'none', background: 'transparent', cursor: 'pointer', padding: 2,
                            margin: '0 auto', display: 'block',
                            color: commentMissing ? 'var(--red)' : 'var(--text-mid)',
                          }}
                        >
                          <CommentIcon filled={!!savedComment} />
                        </button>
                      )}
                    </td>
                    {days.map((day, di) => {
                      const key = keys[di];
                      const entry = row.entriesByDate[key];
                      const cellKey = `${row.key}|${key}`;
                      const isSaving = savingKey === cellKey;
                      return (
                        <td
                          key={key}
                          className={nonWorkingKeys.has(key) ? 'cell-num is-weekend' : 'cell-num'}
                          style={{ borderLeft: '1px solid var(--border)' }}
                        >
                          <TimesheetCell
                            entry={entry}
                            disabled={isEntryApproved(entry) || isEntrySubmitted(entry) || closedKeys.has(key) || isSaving}
                            closed={closedKeys.has(key)}
                            saving={isSaving}
                            dayKey={key}
                            onCommit={(value) => saveCell(row, day, entry, value)}
                          />
                        </td>
                      );
                    })}
                    <td className="cell-num" style={{ fontWeight: 600, borderLeft: '1px solid var(--border)' }}>{weekRowTotal(row, keys)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {row.pinId && (() => {
                        const rowHasApproved = keys.some((k) => isEntryApproved(row.entriesByDate[k]));
                        return (
                          <button
                            title={rowHasApproved ? 'This row has approved hours and can’t be removed' : 'Remove from my timesheet'}
                            disabled={rowHasApproved}
                            onClick={() => unpinRow(row)}
                            style={{
                              border: 'none', background: 'transparent',
                              cursor: rowHasApproved ? 'default' : 'pointer',
                              color: rowHasApproved ? 'var(--border)' : 'var(--text-mid)', fontSize: 14,
                            }}
                          >
                            ×
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                  {commentOpen && (
                    <tr className={gi % 2 === 1 ? 'is-alt' : ''}>
                      <td colSpan={12} style={{ padding: '0 10px 10px' }}>
                        <input
                          className="form-input"
                          autoFocus
                          disabled={commentLocked || savingCommentKey === row.key}
                          placeholder={
                            commentLocked
                              ? 'This week is fully submitted or closed — unsubmit a day to change the comment'
                              : `What was this ${row.taskTypeName} time spent on this week?`
                          }
                          style={{ fontSize: 12, width: '100%' }}
                          maxLength={1000}
                          value={commentValue}
                          onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))}
                          onBlur={(e) => saveComment(row, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                });
              })}
              <tr className="is-group">
                <td colSpan={4}>Total</td>
                {keys.map((key) => (
                  <td
                    key={key}
                    className={nonWorkingKeys.has(key) ? 'cell-num is-weekend' : 'cell-num'}
                    style={{ borderLeft: '1px solid var(--border)' }}
                  >
                    {dayTotalsByKey[key]}
                  </td>
                ))}
                <td className="cell-num" style={{ borderLeft: '1px solid var(--border)' }}>{grandTotal}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={13} style={{ padding: '10px' }}>
                  <form onSubmit={pinRow} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      className="form-select"
                      style={{ fontSize: 12, padding: '6px 10px', width: 220 }}
                      value={newRowProjectId}
                      onChange={(e) => setNewRowProjectId(e.target.value)}
                    >
                      <option value="">+ Add a project…</option>
                      {projectPickerOptions(projects).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <select
                      className="form-select"
                      style={{ fontSize: 12, padding: '6px 10px', width: 200 }}
                      value={newRowTaskTypeId}
                      onChange={(e) => {
                        setNewRowTaskTypeId(e.target.value);
                        setNewRowAccountId('');
                      }}
                      disabled={!newRowProjectId}
                    >
                      <option value="">Task…</option>
                      {availableTaskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    {/* Only for the non-project case (Travel / Post Go Live Support under
                        Administrative Time) -- every other row gets its customer from the project. */}
                    {newRowNeedsAccount && (
                      <select
                        className="form-select"
                        style={{ fontSize: 12, padding: '6px 10px', width: 220 }}
                        value={newRowAccountId}
                        onChange={(e) => setNewRowAccountId(e.target.value)}
                      >
                        <option value="">Customer…</option>
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                    <button
                      type="submit"
                      className="btn btn-outline"
                      style={{ fontSize: 12, padding: '5px 12px' }}
                      disabled={
                        pinning ||
                        !newRowProjectId ||
                        !newRowTaskTypeId ||
                        (newRowNeedsAccount && !newRowAccountId) ||
                        newRowAlreadyPinned
                      }
                    >
                      {pinning ? 'Adding…' : '+ Add row'}
                    </button>
                    {newRowAlreadyPinned && (
                      <span style={{ fontSize: 11, color: 'var(--text-mid)' }}>Already on this timesheet.</span>
                    )}
                  </form>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!loading && <CellLegend />}

      </>)}
    </div>
  );
}

export default TimeEntry;
